import React, { useEffect, useCallback, useRef, useId } from "react";
import { createPortal } from "react-dom";
import type { ModalProps } from "./types";
import { Icon } from "@/components/ui/display/Icon";
import { IconButton } from "@/components/ui/buttons/IconButton";
import styles from "./Modal.module.css";

/**
 * Modal component - A dialog overlay with header, content, and close functionality.
 * Ported from Vue Modal.vue component.
 *
 * WCAG AAA Compliant:
 * - Focus trap keeps keyboard navigation within modal
 * - Focus returns to trigger element on close
 * - Escape key closes modal
 * - aria-modal and aria-labelledby for screen readers
 * - aria-describedby links to content
 */
export const Modal: React.FC<ModalProps> = ({
  isOpen,
  title = "Modal",
  children,
  onClose,
  closeOnOverlayClick = true,
  closeOnEscape = true,
  size = "md",
  className,
  "data-testid": testId,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);
  const generatedId = useId();
  const modalId = testId || `modal-${generatedId}`;

  // Handle escape key
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (closeOnEscape && e.key === "Escape") {
        onClose();
      }
    },
    [closeOnEscape, onClose],
  );

  // Handle overlay click
  const handleOverlayClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (closeOnOverlayClick && e.target === e.currentTarget) {
        onClose();
      }
    },
    [closeOnOverlayClick, onClose],
  );

  // Focus trap - handle tab navigation within modal (WCAG 2.4.3)
  const handleFocusTrap = useCallback((e: KeyboardEvent) => {
    if (e.key !== "Tab" || !modalRef.current) return;

    const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])',
    );

    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0];
    const lastElement = focusableElements[focusableElements.length - 1];

    if (e.shiftKey) {
      // Shift + Tab: if on first element, go to last
      if (document.activeElement === firstElement) {
        e.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: if on last element, go to first
      if (document.activeElement === lastElement) {
        e.preventDefault();
        firstElement.focus();
      }
    }
  }, []);

  // Add/remove event listeners
  useEffect(() => {
    if (isOpen) {
      document.addEventListener("keydown", handleKeyDown);
      document.addEventListener("keydown", handleFocusTrap);
      // Prevent body scroll when modal is open
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("keydown", handleFocusTrap);
      document.body.style.overflow = "";
    };
  }, [isOpen, handleKeyDown, handleFocusTrap]);

  // Store previous focus and restore on close (WCAG 2.4.3)
  useEffect(() => {
    if (isOpen) {
      // Store the element that had focus before modal opened
      previousFocusRef.current = document.activeElement as HTMLElement;

      // Focus the first focusable element (close button) after a brief delay
      requestAnimationFrame(() => {
        const closeButton = modalRef.current?.querySelector<HTMLElement>(
          `[data-testid="${modalId}-close"]`,
        );
        closeButton?.focus();
      });
    } else if (previousFocusRef.current) {
      // Return focus to the element that triggered the modal
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen, modalId]);

  if (!isOpen) {
    return null;
  }

  const modalContent = (
    <div
      className={`${styles.modal}${className ? ` ${className}` : ""}`}
      data-size={size}
      onClick={handleOverlayClick}
      role="presentation"
      data-testid={testId}
    >
      <div
        ref={modalRef}
        data-component="dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`${modalId}-title`}
        aria-describedby={`${modalId}-content`}
        onClick={(e) => e.stopPropagation()}
      >
        <header>
          <h2 data-component="title" id={`${modalId}-title`}>
            {title}
          </h2>
          <IconButton
            icon={<Icon name="close-circle" aria-hidden={true} />}
            variant="close"
            onClick={onClose}
            aria-label="Close modal"
            data-component="close"
            data-testid={`${modalId}-close`}
          />
        </header>
        <div
          data-component="content"
          id={`${modalId}-content`}
          data-testid={`${modalId}-content`}
        >
          {children}
        </div>
      </div>
    </div>
  );

  // Use portal to render modal at document root
  return createPortal(modalContent, document.body);
};

Modal.displayName = "Modal";

export default Modal;
